import { useEffect, useState } from "react";
import { client } from "../net/client";
import { useSignal } from "../net/signal";
import { fa } from "../game/cards";
import { games } from "./games";
import type { MissionState } from "../gen/bazivan/v1/table_pb";

/**
 * «امروز» — بازیِ روز و مأموریت‌های روزانه.
 *
 * هر چیزی که این‌جا نوشته می‌شود از سرور می‌آید: متن، هدف، پیشرفت، و
 * لحظه‌ی پایانِ روز. کلاینت نه منطقه‌ی زمانی حساب می‌کند و نه می‌داند
 * مأموریت‌ها چه بودند — چون هر دو در پیکربندی عوض می‌شوند و کلاینتی که
 * خودش بداند، از روزِ تغییر چیزِ دیگری نشان می‌دهد.
 *
 * تا اولین پاسخ چیزی نشان نمی‌دهد. «مأموریتی نیست» و «هنوز نپرسیده‌ایم» دو
 * چیزند و جای خالی از هر دو صادق‌تر است.
 */
export function Today({ onPlay }: { onPlay: (game: string) => void }) {
  const today = useSignal(client.today);
  const state = useSignal(client.state);

  // با هر وصل‌شدن دوباره می‌پرسیم: نیمه‌شبِ تهران وسطِ یک نشست هم می‌افتد،
  // و بازگشت از خواب یعنی عکسی که ممکن است مالِ دیروز باشد.
  useEffect(() => {
    if (state === "online") client.askToday();
  }, [state]);

  if (!today) return null;
  if (!today.dailyGame && today.missions.length === 0) return null;

  return (
    <section className="plate stack today" aria-label="امروز">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "baseline" }}>
        <b>امروز</b>
        <Countdown endsAtMs={Number(today.endsAtMs)} />
      </div>

      {today.dailyGame && (
        <DailyCard game={today.dailyGame} boost={today.dailyBoost} onPlay={onPlay} />
      )}

      {today.missions.map((m) => (
        <Mission key={m.id} m={m} />
      ))}
    </section>
  );
}

/** کارتِ بازیِ روز. دکمه‌اش مستقیم به همان بازی می‌رود. */
function DailyCard({ game, boost, onPlay }: { game: string; boost: number; onPlay: (g: string) => void }) {
  const entry = games.find((g) => g.id === game);
  return (
    <div className="row daily" style={{ gap: 10, alignItems: "center" }}>
      <span className="stack" style={{ gap: 2, flex: 1 }}>
        <b>بازی روز: {entry?.fa ?? game}</b>
        {/* ضریب فقط روی میزِ رایگان است و همین‌جا گفته می‌شود: وعده‌ی
            دوبرابرِ گلدان، وعده‌ای است که سرور عمداً اجرا نمی‌کند. */}
        <span className="muted">سکه‌ی میز رایگان {fa(boost)} برابر</span>
      </span>
      <button className="hb on" onClick={() => onPlay(game)}>بازی کن</button>
    </div>
  );
}

/** یک مأموریت با نوارِ پیشرفت و دکمه‌ی گرفتن. */
function Mission({ m }: { m: MissionState }) {
  const done = m.progress >= m.target;
  const pct = m.target > 0 ? Math.min(100, Math.round((m.progress / m.target) * 100)) : 0;
  return (
    <div className="stack mission" style={{ gap: 4 }}>
      <div className="row" style={{ justifyContent: "space-between", gap: 8 }}>
        <span>{m.title || missionText(m)}</span>
        <span className="muted">{fa(m.progress)}/{fa(m.target)}</span>
      </div>
      <span className="mbar"><span style={{ width: pct + "%" }} /></span>
      <div className="row" style={{ justifyContent: "space-between", gap: 8 }}>
        <span className="muted">{rewardText(m)}</span>
        {m.claimed ? (
          <span className="muted">گرفته شد</span>
        ) : (
          <button className="hb" disabled={!done} onClick={() => client.claimMission(m.id)}>
            بگیر
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * متنِ مأموریت وقتی سرور عنوانی ننوشته.
 *
 * پیش‌فرض است و نه جایگزین: عنوانِ پیکربندی همیشه می‌چربد، چون آن‌جا
 * می‌شود چیزی نوشت که این تابع هیچ‌وقت نمی‌تواند بسازد.
 */
export function missionText(m: MissionState): string {
  const n = fa(m.target);
  switch (m.kind) {
    case "win": return `${n} دست ببر`;
    case "play_game": return `${n} دست ${games.find((g) => g.id === m.game)?.fa ?? m.game} بازی کن`;
    case "daily_game": return "بازی روز را بازی کن";
    default: return `${n} دست بازی کن`;
  }
}

/** جایزه، به فارسی. خالی نمی‌مانَد: مأموریتِ بی‌جایزه چیزی برای گفتن ندارد. */
export function rewardText(m: MissionState): string {
  const bits: string[] = [];
  if (m.rewardCoins > 0n) bits.push(`${fa(m.rewardCoins)} سکه`);
  if (m.rewardGems > 0n) bits.push(`${fa(m.rewardGems)} گوهر`);
  if (m.rewardXp > 0) bits.push(`${fa(m.rewardXp)} تجربه`);
  return bits.join(" · ");
}

/**
 * شمارشِ معکوس تا نیمه‌شبِ **تهران**.
 *
 * لحظه‌ی پایان از سرور می‌آید و این‌جا فقط تفریق می‌شود: مرورگرِ بازیکن
 * ممکن است در هر منطقه‌ی زمانی باشد، و ساعتش را هم می‌تواند عوض کند.
 */
function Countdown({ endsAtMs }: { endsAtMs: number }) {
  // مقدارِ اولیه فقط برای فریمِ اول است — افکت بلافاصله بازنویسی‌اش می‌کند.
  // بی آن، شمارش یک فریم خالی می‌مانَد؛ و چون افکت مرجع است، آزمون (که
  // بعد از افکت می‌خوانَد) این خط را نمی‌سنجد و نباید منطقِ تازه‌ای در آن
  // نوشته شود.
  const [left, setLeft] = useState(() => endsAtMs - Date.now());
  useEffect(() => {
    setLeft(endsAtMs - Date.now());
    const t = setInterval(() => setLeft(endsAtMs - Date.now()), 30_000);
    return () => clearInterval(t);
  }, [endsAtMs]);

  if (left <= 0) return <span className="muted">روز تازه</span>;
  const h = Math.floor(left / 3_600_000);
  const m = Math.floor((left % 3_600_000) / 60_000);
  return <span className="muted">{h > 0 ? `${fa(h)} ساعت` : `${fa(m)} دقیقه`} مانده</span>;
}
